import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentRef,
  type ReactNode,
} from 'react';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { createPortal } from 'react-dom';
import {
  Colors,
  Flex,
  IconMore,
  IconTrash,
  IconWrite,
  Typography,
} from '@banhala/ui-kit';
import type { LikeFolder } from 'shared/src/api/likedFolderService/getLikedFolders/types';

interface FolderMoreMenuProps {
  folder: LikeFolder;
  onPressRename: (folder: LikeFolder) => void;
  onPressDelete: (folder: LikeFolder) => void;
}

interface MenuItem {
  label: string;
  color: 'content_primary' | 'content_negative';
  icon: ReactNode;
  onPress: () => void;
}

const MENU_WIDTH = 200;
const MENU_HEIGHT_EST = 112; // 2행 기준 추정치, 하단 넘침 시 위로 띄우는 판단에만 사용
const EDGE = 8; // 뷰포트 가장자리 여백
const GAP = 4; // 트리거-메뉴 간격

/**
 * 웹 전용 FolderMoreMenu.
 *
 * 네이티브(`FolderMoreMenu.tsx`)는 react-native-popup-menu의 `Menu`를 쓰지만,
 * 웹에서는 팝업 위치가 어긋난다. react-native-popup-menu는 트리거 위치를
 * react-native-web의 `measure`(= `getRect`, `offsetLeft` 체인)로 재는데 이는
 * CSS transform을 무시한다. 찜 화면의 `TabView`(react-native-tab-view 웹 Pager)는
 * 모든 탭을 가로로 펼친 뒤 활성 탭을 `transform: translateX(-index*width)`로
 * 끌어와 보여주므로, '내 서랍' 탭(index 1)의 트리거 측정 x가 한 화면 너비만큼
 * 어긋나 메뉴가 우측으로 밀린다(루트의 `MenuProvider`는 transform 바깥).
 *
 * → 트리거를 transform-aware한 `measureInWindow`(= `getBoundingClientRect` 기반)로
 *   측정하고, 메뉴를 `createPortal(document.body)`로 transform 바깥에 `fixed`로
 *   띄워 회피한다. (@banhala/ui-kit `ModalProvider.web`이 동일 문제를 푸는 방식)
 */
export const FolderMoreMenu = ({
  folder,
  onPressRename,
  onPressDelete,
}: FolderMoreMenuProps) => {
  const triggerRef = useRef<ComponentRef<typeof Flex>>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );

  const close = useCallback(() => setCoords(null), []);

  const open = useCallback(() => {
    triggerRef.current?.measureInWindow(
      (x: number, y: number, width: number, height: number) => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        // 메뉴 우측을 트리거 우측에 정렬 + 가로 뷰포트 클램프
        const left = Math.min(
          Math.max(x + width - MENU_WIDTH, EDGE),
          vw - EDGE - MENU_WIDTH,
        );
        // 트리거 아래로 펼치되, 하단을 넘으면 위로 띄움
        let top = y + height + GAP;
        if (top + MENU_HEIGHT_EST > vh - EDGE) {
          top = y - GAP - MENU_HEIGHT_EST;
        }
        setCoords({ top: Math.max(top, EDGE), left });
      },
    );
  }, []);

  // fixed 좌표는 스크롤/리사이즈 시 어긋나므로 닫는다.
  useEffect(() => {
    if (coords === null) return;
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [coords, close]);

  const items: MenuItem[] = [
    {
      label: '서랍 이름 바꾸기',
      color: 'content_primary',
      icon: <IconWrite size={24} fill={Colors.content_primary} />,
      onPress: () => onPressRename(folder),
    },
    {
      label: '서랍 삭제',
      color: 'content_negative',
      icon: <IconTrash size={24} fill={Colors.content_negative} />,
      onPress: () => onPressDelete(folder),
    },
  ];

  return (
    // 카드 onPress(폴더 열기) 터치 전파 차단용
    <Flex onPress={() => undefined}>
      <Flex
        ref={triggerRef}
        padding={{ left: 12, vertical: 6 }}
        onPress={open}
        testID={'서랍더보기버튼'}
      >
        <IconMore size={16} fill={Colors.content_tertiary} />
      </Flex>
      {coords && typeof document !== 'undefined'
        ? createPortal(
            <>
              <Pressable onPress={close} style={styles.backdrop} />
              <View style={[styles.menu, { top: coords.top, left: coords.left }]}>
                {items.map((item) => (
                  <Flex.Row
                    key={item.label}
                    onPress={() => {
                      close();
                      item.onPress();
                    }}
                    padding={{ horizontal: 16, vertical: 14 }}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    gap={12}
                  >
                    <Typography
                      type={'body2'}
                      color={item.color}
                      numberOfLines={1}
                    >
                      {item.label}
                    </Typography>
                    {item.icon}
                  </Flex.Row>
                ))}
              </View>
            </>,
            document.body,
          )
        : null}
    </Flex>
  );
};

const styles = StyleSheet.create({
  // position: 'fixed'는 RN 타입엔 없지만 react-native-web에서 유효(웹 전용 파일)
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
  } as unknown as ViewStyle,
  menu: {
    position: 'fixed',
    zIndex: 9999,
    width: MENU_WIDTH,
    backgroundColor: Colors.bg_primary,
    borderRadius: 12,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
  } as unknown as ViewStyle,
});
